import { Board } from '~/components/Shared/Layout/Board/Board';
import UserEventsTable from '../UserEventsTable/UserEventsTable';
import { useEffect, useMemo, useState } from 'react';
import { IconButton, Pagination, Spinner } from '~/ui';
import { useTranslation } from 'react-i18next';
import FiltersIcon from '~/assets/svg/newSvg/filter.svg?react';

import styles from './EventsBoard.module.scss';
import EventTableFilters from '../EventTableFilters/EventTableFilters';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { getPaginate } from '~/utils/getPaginate';
import { paginateEnums } from '~/utils/types/paginateEnums';

interface SelectedPage {
  selected: number;
}

interface IFilters {
  events: string[];
  type_ids: number[];
  dates: [Dayjs | null, Dayjs | null];
}

interface Points {
  page: number;
}

const UserEventsBoard = () => {
  const { t } = useTranslation();
  const { userID } = useParams();
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'event-user-points',
      exclude: [],
    });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { value, paginate } = getPaginate(paginateEnums.USER_EVENT_PAGINATE);
  const [page, setPage] = useState<number>(paginate);
  const [searchParams] = useSearchParams();
  const dateParams = searchParams.get('date');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<IFilters>({
    events: [],
    type_ids: [],
    dates: [null, null],
  });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });

  const params = useMemo(() => {
    return {
      page,
      perPage,
      user_ids: [userID],
      events: filters.events,
      type_ids: filters.type_ids,
      dates: [
        filters.dates[0]
          ? filters.dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
          : null,
        filters.dates[1]
          ? filters.dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
          : null,
      ],
      field: sort.field || '',
      sort: sort.direction || '',
    };
  }, [filters, perPage, page, sort]);

  const { data: events, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/event-logs', {
        params,
      });
      return data;
    },
    queryKey: ['events', params, page, perPage],
    keepPreviousData: true,
  });

  useEffect(() => {
    setPage(1);
  }, [perPage, filters]);

  useEffect(() => {
    if (dateParams) {
      const start = dayjs(dateParams, 'DD.MM.YYYY').startOf('day');
      const end = dayjs(dateParams, 'DD.MM.YYYY').endOf('day');
      setFilters(prev => ({ ...prev, dates: [start, end] }));
    }
  }, [dateParams]);

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  return (
    <Board classNames={styles.board}>
      <div className={styles.actionWrapper}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <IconButton
          className={styles.filtersButton}
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
        >
          <FiltersIcon />{' '}
          <span className={styles.filtersButtonText}>
            {isFiltersOpen ? `${t('hide_filters')}` : `${t('show_filters')}`}
          </span>
        </IconButton>
      </div>
      {isFiltersOpen ? <EventTableFilters setFilters={setFilters} /> : null}
      {events && !isLoading ? (
        <UserEventsTable sort={sort} setSort={setSort} data={events?.data} />
      ) : (
        <Spinner />
      )}
      {events?.data.length ? (
        <div className={styles.eventsTablePagination}>
          <TableAmountPage
            firstRow={events?.from}
            lastRow={events?.to}
            total={events?.total}
          />
          <Pagination
            forcePage={page}
            searchParam={value}
            pageCount={events?.last_page}
            onPageChange={(selectedPage: SelectedPage) => {
              setPage(selectedPage.selected + 1);
            }}
          />
        </div>
      ) : null}
    </Board>
  );
};

export default UserEventsBoard;
